import { Archive, Star, CheckSquare, MoreHorizontal, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Thumb } from "@/components/Thumb";
import { STATUS_META } from "@/components/StatusBadge";
import { useItemsStore } from "@/lib/stores/itemsStore";
import { splitMeta, formatWhen, STATUS_DOT } from "@/lib/format";

const QuickAction = ({ icon: Icon, label, onClick, active }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={cn(
      "w-7 h-7 rounded-md border bg-card grid place-items-center transition-colors",
      active ? "text-primary border-primary/40" : "text-muted-foreground hover:text-foreground hover:border-teal-300/60",
    )}
  >
    <Icon className="h-3.5 w-3.5" strokeWidth={1.75} fill={active ? "currentColor" : "none"} />
  </button>
);

export const ItemRow = ({ item, selected, onClick, flush, showActions = true }) => {
  const { setStatus, createAction } = useItemsStore();
  const { author } = splitMeta(item.meta);
  const enriching = item.analyzeStatus === "enriqueciendo";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
      data-testid={`item-row-${item.id}`}
      className={cn(
        "group w-full text-left flex items-start gap-3 px-3 py-3 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        flush ? "rounded-none" : "rounded-lg border",
        selected
          ? "bg-accent " + (flush ? "shadow-[inset_2px_0_0_0_hsl(var(--primary))]" : "border-primary/40")
          : flush
            ? "hover:bg-secondary/60"
            : "border-transparent hover:bg-secondary/60",
      )}
    >
      <Thumb platform={item.platform} size="row" />

      <div className="flex-1 min-w-0">
        <p className="font-medium text-[15px] leading-snug truncate">{item.title}</p>
        <p className="mt-0.5 text-[13px] text-muted-foreground truncate">
          {item.platform}
          {author ? ` · ${author}` : item.mainTopic ? ` · ${item.mainTopic}` : ""}
          {enriching && (
            <span className="ml-2 inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
              <Loader2 className="h-3 w-3 animate-spin" /> enriqueciendo…
            </span>
          )}
          {item.analyzeStatus === "error" && (
            <span className="ml-2 text-amber-700 dark:text-amber-400">pendiente de enriquecer</span>
          )}
        </p>
        {item.tags.length > 0 && (
          <div className="mt-1.5 flex gap-1.5 flex-wrap">
            {item.tags.slice(0, 3).map((t) => (
              <span key={t} className="rounded-md bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 flex flex-col items-end gap-1.5 self-stretch justify-between py-0.5">
        <span className="text-xs text-muted-foreground tabular-nums">{formatWhen(item.createdAt)}</span>
        {showActions ? (
          <div className="hidden md:flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <QuickAction
              icon={Archive}
              label="Archivar"
              onClick={() => {
                setStatus(item.id, "archivado");
                toast("Contenido archivado.");
              }}
            />
            <QuickAction
              icon={Star}
              label="Marcar importante"
              active={item.status === "importante"}
              onClick={() => setStatus(item.id, item.status === "importante" ? "inbox" : "importante")}
            />
            <QuickAction
              icon={CheckSquare}
              label="Crear tarea"
              onClick={() => {
                createAction(item.id, "tarea");
                toast.success("Tarea creada. La verás en Tareas.");
              }}
            />
            <QuickAction icon={MoreHorizontal} label="Más acciones" onClick={() => toast("Más acciones en la versión final.")} />
          </div>
        ) : null}
        <span
          className={cn(
            "flex items-center gap-1.5 text-[11px] font-medium",
            item.status === "pendiente" && "text-amber-700 dark:text-amber-400",
            item.status === "importante" && "text-teal-700 dark:text-teal-400",
            item.status === "aplicado" && "text-green-700 dark:text-green-400",
            (item.status === "inbox" || item.status === "archivado") && "text-muted-foreground",
            showActions && "md:group-hover:hidden",
          )}
        >
          <span className={cn("w-1.5 h-1.5 rounded-full", STATUS_DOT[item.status])} />
          {STATUS_META[item.status]?.label}
        </span>
      </div>
    </div>
  );
};
